import * as React from 'react';
import { Clock } from 'lucide-react';

import { formatTimeWindowDotted } from '@klareospace/pharos-lib-front-utils';
import type { TimeSlot } from '@klareospace/pharos-lib-types';
import { HoverActionField, IconText, TextCell } from '@klareospace/pharos-lib-web-utils';

import type { OrderFieldEditState, OrderFieldLockReason } from '../hooks/use-order-editability';
import { EditableFieldWrapper } from './editable-field-wrapper';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TimeSlotFieldProps {
  /**
   * When provided (with `onEdit`), renders via EditableFieldWrapper — hidden/locked/editable
   * per useOrderEditability. Omit for a purely static row (e.g. the non-transport-type-matching
   * timeslot, which is never editable regardless of permission).
   */
  fieldState?: OrderFieldEditState;
  lockReason?: OrderFieldLockReason | null;
  /** Called when the user clicks the edit affordance. Omit to render as static. */
  onEdit?: () => void;
  /** The time slot to display; renders '—' when undefined. */
  timeSlot?: TimeSlot;
  /** Optional accessible title for the HoverActionField button. */
  title?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Displays a time window inside a HoverActionField with a clock icon.
 * Renders an em-dash placeholder when `timeSlot` is undefined.
 *
 * Replaces the repeated `HoverActionField > Clock + formatTimeWindow || '—'` pattern
 * used in OrderDetailSection and PickupDeliverySection.
 */
export function TimeSlotField({
  fieldState,
  lockReason,
  onEdit,
  timeSlot,
  title,
}: TimeSlotFieldProps): React.JSX.Element {
  const content =
    timeSlot !== undefined ? (
      <IconText icon={Clock} size="sm">
        <span className="truncate text-foreground">{formatTimeWindowDotted(timeSlot)}</span>
      </IconText>
    ) : (
      <TextCell>—</TextCell>
    );

  if (fieldState !== undefined && onEdit !== undefined) {
    return (
      <EditableFieldWrapper fieldState={fieldState} lockReason={lockReason ?? null} onEdit={onEdit}>
        {content}
      </EditableFieldWrapper>
    );
  }

  return (
    <HoverActionField onClick={onEdit} title={title}>
      {content}
    </HoverActionField>
  );
}
